import { h } from "snabbdom";
import type { VNode } from "snabbdom";
import type { Card, Document, Msg } from "../types";
import { EditorState } from "prosemirror-state";
import { parse } from "./serialization";
import { buildPlugins } from "./plugins";
import { actions } from "./toolbar";
import { refreshAllViewBlocks } from "./view-node-view";
import { destroyMainEditor, mountMainEditor, onFocusChange, syncEditors, toolbarEditor } from "./card-editors";
import { notifyWatchers } from "./cards";
import { button } from "./dom";

export { serialize } from "./serialization";
export { shownCardIds } from "./card-editors";

// The toolbar of each main card has a function that sets the state of its
// buttons. syncEditorViews and each change of the focus call these functions.
const toolbarRefreshers = new Map<string, () => void>();

function refreshToolbars(): void {
  for (const refresh of toolbarRefreshers.values()) refresh();
}

onFocusChange(refreshToolbars);

// Build the initial EditorState for a card's content. Pure construction — the
// app Model owns the result.
export function createEditorState(content: Document): EditorState {
  return EditorState.create({
    doc: parse(content),
    plugins: buildPlugins(),
  });
}

// The toolbar of a card's page. It changes the editor with the focus, which can
// be the editor of a section.
function renderToolbar(cardId: string, container: HTMLElement, dispatch: (msg: Msg) => void): void {
  container.innerHTML = "";
  const toolbar = document.createElement("div");
  toolbar.className = "editor-toolbar";
  const buttons: {
    el: HTMLButtonElement;
    active: ((s: EditorState) => boolean) | undefined;
    enabled: ((s: EditorState) => boolean) | undefined;
  }[] = [];
  for (const action of actions) {
    const btn = button("editor-toolbar__btn", () => {
      const editor = toolbarEditor(cardId);
      if (editor?.view) action.command(editor.view, editor.cardId, dispatch);
    });
    btn.title = action.title;
    btn.textContent = action.label;
    toolbar.appendChild(btn);
    buttons.push({ el: btn, active: action.active, enabled: action.enabled });
  }
  container.appendChild(toolbar);

  const refresh = () => {
    const state = toolbarEditor(cardId)?.view?.state;
    for (const { el, active, enabled } of buttons) {
      el.classList.toggle("editor-toolbar__btn--active", state && active ? active(state) : false);
      el.disabled = !state || (enabled ? !enabled(state) : false);
    }
  };
  refresh();
  toolbarRefreshers.set(cardId, refresh);
}

// Make the editors, the node views, and the toolbars agree with the Model that
// render() published. Call after each patch.
export function syncEditorViews(): void {
  syncEditors();
  notifyWatchers();
  refreshToolbars();
}

// Stop the main editor and the toolbar of a deleted card.
export function destroyEditor(cardId: string): void {
  destroyMainEditor(cardId);
  toolbarRefreshers.delete(cardId);
}

export function mountEditor(card: Card, dispatch: (msg: Msg) => void): VNode {
  return h("div.editor-wrap", { key: card.id }, [
    h("div.editor-toolbar-mount", { key: `toolbar-${card.id}` }),
    h("div.editor-container", {
      key: `editor-${card.id}`,
      hook: {
        insert(vnode) {
          const el = vnode.elm as HTMLElement;
          mountMainEditor(card.id, el, dispatch);
          const toolbarEl = el.previousElementSibling as HTMLElement | null;
          if (toolbarEl) renderToolbar(card.id, toolbarEl, dispatch);
          refreshAllViewBlocks();
        },
      },
    }),
  ]);
}
